/**
 * The tax exempt certificate's choices (piece 10; decision 0306): the state
 * from the fifty-one codes with its name, the exemption reason from the
 * multistate list, and the readings that pick both off a certificate's text.
 * 🔨 The reason list is the Streamlined Sales Tax Certificate of Exemption's
 * own reason set, which the 24 member states accept and the others name in
 * their own forms; a state that narrows it does so here, in one map, never
 * in a screen.
 */
export const US_STATE_NAMES: Readonly<Record<string, string>> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida',
  GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

/** The five states with no general sales tax; a certificate there is for a local or a special tax, and the list still offers them. */
export const NO_SALES_TAX_STATES: readonly string[] = ['AK', 'DE', 'MT', 'NH', 'OR'];

export interface ExemptionReason {
  code: string;
  label: string;
  /** Words a certificate's text carries when it claims this reason, lower-cased. */
  cues: readonly string[];
}

/** The multistate reason set, in the order the form lists them. */
export const EXEMPTION_REASONS: readonly ExemptionReason[] = [
  { code: 'FederalGovernment', label: 'Federal government', cues: ['federal government', 'united states government', 'u.s. government'] },
  { code: 'StateLocalGovernment', label: 'State or local government', cues: ['state government', 'local government', 'political subdivision', 'municipality', 'county government', 'school district'] },
  { code: 'TribalGovernment', label: 'Tribal government', cues: ['tribal government', 'indian tribe', 'tribe'] },
  { code: 'ForeignDiplomat', label: 'Foreign diplomat', cues: ['foreign diplomat', 'diplomatic'] },
  { code: 'Charitable', label: 'Charitable organization', cues: ['charitable', '501(c)(3)', 'nonprofit', 'non-profit'] },
  { code: 'Religious', label: 'Religious organization', cues: ['religious', 'church'] },
  { code: 'Educational', label: 'Educational organization', cues: ['educational', 'school', 'university', 'college'] },
  { code: 'Resale', label: 'Resale', cues: ['resale', 'resell', 'resold', 'reseller', 'wholesale'] },
  { code: 'Agricultural', label: 'Agricultural production', cues: ['agricultural', 'farm', 'farming', 'agriculture'] },
  { code: 'Manufacturing', label: 'Industrial production or manufacturing', cues: ['manufactur', 'industrial production', 'processing'] },
  { code: 'DirectPay', label: 'Direct pay permit', cues: ['direct pay'] },
  { code: 'DirectMail', label: 'Direct mail', cues: ['direct mail'] },
  { code: 'Other', label: 'Other', cues: [] },
];

/**
 * A state's reasons: the multistate set, narrowed where the state's own
 * general exemption certificate offers no line for a reason (decision 0347;
 * read off the committed forms, 2026-09-12). Only a state whose committed
 * form is its general certificate with a printed list of reasons narrows;
 * a state whose committed form is a single-purpose certificate keeps the
 * whole set, and Other stays everywhere. Kept as a map so the narrowing is
 * data a reader checks against the form, never a screen's rule.
 */
export const STATE_REASON_EXCLUSIONS: Readonly<Record<string, readonly string[]>> = {
  // ST-5: resale, government, the University System and carriers; no tribal, diplomat, church, charity, farm, direct pay or direct mail line.
  GA: ['TribalGovernment', 'ForeignDiplomat', 'Charitable', 'Religious', 'Agricultural', 'DirectPay', 'DirectMail'],
  // 31-014: the form's own instructions say churches are not exempt; no tribal, diplomat or direct mail line.
  IA: ['TribalGovernment', 'ForeignDiplomat', 'Religious', 'DirectMail'],
  // ST-101: no diplomat, direct pay or direct mail line.
  ID: ['ForeignDiplomat', 'DirectPay', 'DirectMail'],
  // ST-105: manufacturing, nonprofit, resale, farmers, contractor, state and federal government, other.
  IN: ['TribalGovernment', 'ForeignDiplomat', 'DirectPay', 'DirectMail'],
  // 3372: government entity, nonprofit school and hospital, church, 501(c)(3), resale, agricultural, industrial processing, direct pay.
  MI: ['TribalGovernment', 'ForeignDiplomat', 'DirectMail'],
  // Form 149: resale, wholesale, manufacturing, agricultural, carriers; an exempt entity carries a letter the form does not list.
  MO: ['FederalGovernment', 'StateLocalGovernment', 'TribalGovernment', 'ForeignDiplomat', 'Charitable', 'Religious', 'Educational', 'DirectPay', 'DirectMail'],
  // Form 13: government units, exempt organizations, manufacturing machinery, resale; no tribal, diplomat, direct pay or direct mail category.
  NE: ['TribalGovernment', 'ForeignDiplomat', 'DirectPay', 'DirectMail'],
  // REV-1220: every reason but a tribal government and direct mail.
  PA: ['TribalGovernment', 'DirectMail'],
  // ST-8 with ST-8A: federal and state government, charities, schools, agriculture, manufacturing, resale; no tribal, diplomat, church, direct pay or direct mail line.
  SC: ['TribalGovernment', 'ForeignDiplomat', 'Religious', 'DirectPay', 'DirectMail'],
  // TC-721 with TC-721G: every reason but a direct pay permit.
  UT: ['DirectPay'],
};

export function exemptionReasonsFor(stateCode: string): readonly ExemptionReason[] {
  const excluded = STATE_REASON_EXCLUSIONS[stateCode.toUpperCase()] ?? [];
  return EXEMPTION_REASONS.filter((reason) => !excluded.includes(reason.code));
}

export function exemptionReasonLabel(code: string): string {
  return EXEMPTION_REASONS.find((reason) => reason.code === code)?.label ?? code;
}

/**
 * The form numbers a state prints on its own certificate, read before the
 * state's name: Ohio's STEC B says Ohio nowhere on the page, and a form
 * number is a stronger signal than a name that also appears in an address
 * (found while building against the committed forms, 2026-09-12). Only
 * codes no other state's form shares are listed.
 */
export const STATE_FORM_CODES: Readonly<Record<string, readonly string[]>> = {
  OH: ['STEC B', 'STEC-B', 'STEC U', 'STEC-U'],
  CA: ['CDTFA-230', 'CDTFA 230'],
  PA: ['REV-1220'],
  TX: ['01-339'],
  IL: ['CRT-61'],
  IN: ['ST-105'],
  IA: ['31-014'],
  UT: ['TC-721G', 'TC-721'],
  TN: ['RV-F1301301'],
  HI: ['G-17'],
  CT: ['CERT-100'],
  KS: ['PR-78', 'ST-28A', 'ST-28G'],
  KY: ['51A126', '51A105'],
  ID: ['ST-101'],
  NY: ['ST-120.1', 'ST-120', 'ST-121'],
  MI: ['Form 3372'],
  MO: ['Form 149'],
  AZ: ['Form 5000'],
};

/**
 * The form number a certificate's text carries, the longest match first so
 * TC-721G is not read as TC-721; null where no state's form number is on
 * the page. A form template is keyed on it (decision 0347).
 */
export function detectFormCode(text: string): string | null {
  const codes = Object.values(STATE_FORM_CODES).flat().sort((a, b) => b.length - a.length);
  return codes.find((form) => formCodePattern(form).test(text)) ?? null;
}

function formCodePattern(form: string): RegExp {
  return new RegExp(`(?<![A-Z0-9-])${form.replace(/[-\s]/g, '[-\\s]?')}(?![A-Z0-9])`, 'i');
}

/** Words a multistate certificate carries; the state on one is the purchaser's to pick, never read off the page. */
export const MULTISTATE_FORM_CUES: readonly string[] = ['streamlined sales tax', 'streamlined sales and use tax', 'multijurisdiction', 'uniform sales & use tax'];

export function isMultistateForm(text: string): boolean {
  const lower = text.toLowerCase();
  return MULTISTATE_FORM_CUES.some((cue) => lower.includes(cue));
}

/**
 * The state a certificate's text names: its own form number first, then the
 * state whose full name appears most, with the bare code counted only where
 * it stands beside "state" or a ZIP. A multistate form and nothing found
 * both answer null; the person picks.
 */
export function detectStateCode(text: string): string | null {
  for (const [code, forms] of Object.entries(STATE_FORM_CODES)) {
    if (forms.some((form) => formCodePattern(form).test(text))) return code;
  }
  if (isMultistateForm(text)) return null;
  const lower = text.toLowerCase();
  let best: { code: string; hits: number } | null = null;
  for (const [code, name] of Object.entries(US_STATE_NAMES)) {
    const nameHits = lower.split(name.toLowerCase()).length - 1;
    const codeHits = (text.match(new RegExp(`(?:state\\s*(?:of)?\\s*[:.]?\\s*|,\\s*)${code}\\b|\\b${code}\\s+\\d{5}\\b`, 'g')) ?? []).length;
    const hits = nameHits * 2 + codeHits;
    if (hits > 0 && (best === null || hits > best.hits)) best = { code, hits };
  }
  return best?.code ?? null;
}

/** The reason a certificate's text claims: the reason whose cue appears first on the page. */
export function detectExemptionReason(text: string): string | null {
  const lower = text.toLowerCase();
  let best: { code: string; at: number } | null = null;
  for (const reason of EXEMPTION_REASONS) {
    for (const cue of reason.cues) {
      const at = lower.indexOf(cue);
      if (at >= 0 && (best === null || at < best.at)) best = { code: reason.code, at };
    }
  }
  return best?.code ?? null;
}

/**
 * A form committed to `docs/reference/tax-exempt-forms` (decision 0676): its
 * file in that folder, the form's own code, and what the form is. The code is
 * the value a certificate's `formType?` holds, and matches the code the
 * reading's template names for the same form.
 */
export interface CommittedExemptionForm {
  file: string;
  code: string;
  title: string;
}

/** A state's exemption form and where the revenue site keeps it; the five no-sales-tax states name none. */
export interface StateExemptionForm {
  form: string;
  url: string;
  /** The state's forms committed to the repository, each served by the forms library (decisions 0676, 0677). */
  committed?: readonly CommittedExemptionForm[];
}

const cf = (file: string, code: string, title: string): CommittedExemptionForm => ({ file, code, title });

/**
 * Where a rep gets the blank form for the state a certificate is for
 * (product owner, 2026-09-12: the forms repository held links and no way to
 * reach them from the product). One row per state, the multistate
 * Streamlined certificate where a member state uses it, and the revenue
 * site's own page rather than a PDF address, since a state moves its PDFs.
 * The same table is the README of docs/reference/tax-exempt-forms.
 */
export const STATE_EXEMPTION_FORMS: Readonly<Record<string, StateExemptionForm>> = {
  AL: { form: 'Form ST-EX-A1', url: 'https://www.revenue.alabama.gov/sales-use/exemption-certificates/' },
  AZ: { form: 'Form 5000 Transaction Privilege Tax Exemption Certificate', url: 'https://azdor.gov/forms/tpt-forms/transaction-privilege-tax-exemption-certificate', committed: [cf('AZ-5000a.pdf', 'Form 5000', 'Form 5000A Arizona Resale Certificate')] },
  AR: { form: 'Form ST391 Exemption Certificate', url: 'https://www.dfa.arkansas.gov/excise-tax/sales-and-use-tax/sales-and-use-tax-forms/' },
  CA: { form: 'CDTFA-230 General Resale Certificate', url: 'https://www.cdtfa.ca.gov/formspubs/cdtfa230.pdf', committed: [cf('CA-cdtfa-230.pdf', 'CDTFA-230', 'CDTFA-230 General Resale Certificate')] },
  CO: { form: 'DR 0563 Sales Tax Exemption Certificate', url: 'https://tax.colorado.gov/sales-tax-exemption-certificate' },
  CT: { form: 'CERT-100 Resale Certificate', url: 'https://portal.ct.gov/drs/sales-tax/exemption-certificates', committed: [cf('CT-cert-100.pdf', 'CERT-100', 'CERT-100 Materials, Tools, and Fuel')] },
  DC: { form: 'FR-368 Certificate of Resale', url: 'https://otr.cfo.dc.gov/page/sales-and-use-tax-exemptions' },
  FL: { form: 'DR-13 Annual Resale Certificate', url: 'https://floridarevenue.com/taxes/taxesfees/Pages/sales_tax.aspx' },
  GA: { form: 'ST-5 Sales Tax Certificate of Exemption', url: 'https://dor.georgia.gov/st-5-sales-tax-certificate-exemption', committed: [cf('GA-st-5.pdf', 'ST-5', 'ST-5 Sales Tax Certificate of Exemption')] },
  HI: { form: 'G-17 Resale Certificate for Goods', url: 'https://tax.hawaii.gov/forms/a1_b1_1gewgexcise/', committed: [cf('HI-g-17.pdf', 'G-17', 'G-17 Resale Certificate for Goods')] },
  ID: { form: 'ST-101 Sales Tax Resale or Exemption Certificate', url: 'https://tax.idaho.gov/forms/efo00149-st-101/', committed: [cf('ID-st-101.pdf', 'ST-101', 'ST-101 Sales Tax Resale or Exemption Certificate')] },
  IL: { form: 'CRT-61 Certificate of Resale', url: 'https://tax.illinois.gov/forms/sales/crt-61.html', committed: [cf('IL-crt-61.pdf', 'CRT-61', 'CRT-61 Certificate of Resale')] },
  IN: { form: 'ST-105 General Sales Tax Exemption Certificate', url: 'https://www.in.gov/dor/tax-forms/sales-tax-forms/', committed: [cf('IN-st-105.pdf', 'ST-105', 'ST-105 General Sales Tax Exemption Certificate')] },
  IA: { form: 'Iowa Sales Tax Exemption Certificate (31-014)', url: 'https://tax.iowa.gov/forms/iowa-sales-tax-exemption-certificate', committed: [cf('IA-31-014.pdf', '31-014', '31-014 Sales/Use/Excise Tax Exemption Certificate (2025)'), cf('IA-31-014-rev-2019.pdf', '31-014', '31-014 Sales/Use/Excise Tax Exemption Certificate (2019)')] },
  KS: { form: 'ST-28A Resale Exemption Certificate', url: 'https://www.ksrevenue.gov/forms-btsalesexempt.html', committed: [cf('KS-st-28a.pdf', 'ST-28A', 'ST-28A Resale Exemption Certificate'), cf('KS-st-28g.pdf', 'ST-28G', 'ST-28G Federal Agency Exemption Certificate'), cf('KS-pr-78ed.pdf', 'PR-78ED', 'PR-78ED Entity Exemption Certificate'), cf('KS-pr-78ks.pdf', 'PR-78KS', 'PR-78KS Entity Exemption Certificate')] },
  KY: { form: 'Form 51A105 Resale Certificate', url: 'https://revenue.ky.gov/Forms/Pages/Sales-and-Use-Tax-Forms.aspx', committed: [cf('KY-51a126.pdf', '51A126', '51A126 Purchase Exemption Certificate')] },
  LA: { form: 'R-1064 Resale Certificate', url: 'https://revenue.louisiana.gov/Forms/ForBusinesses' },
  ME: { form: 'ST-A-119 Resale Certificate', url: 'https://www.maine.gov/revenue/taxes/sales-use-service-provider-tax' },
  MD: { form: 'Sales and Use Tax Exemption Certificate', url: 'https://www.marylandtaxes.gov/business/sales-use/index.php' },
  MA: { form: 'ST-4 Sales Tax Resale Certificate', url: 'https://www.mass.gov/lists/sales-and-use-tax-forms' },
  MI: { form: 'Form 3372 Sales and Use Tax Certificate of Exemption', url: 'https://www.michigan.gov/taxes/business-taxes/sales-use-tax/forms', committed: [cf('MI-3372.pdf', 'Form 3372', 'Form 3372 Sales and Use Tax Certificate of Exemption')] },
  MN: { form: 'ST3 Certificate of Exemption', url: 'https://www.revenue.state.mn.us/form-st3-certificate-exemption' },
  MS: { form: 'Sales tax permit and exemption letters', url: 'https://www.dor.ms.gov/business/sales-and-use-tax' },
  MO: { form: 'Form 149 Sales and Use Tax Exemption Certificate', url: 'https://dor.mo.gov/forms/149.pdf', committed: [cf('MO-149.pdf', 'Form 149', 'Form 149 Sales and Use Tax Exemption Certificate')] },
  NE: { form: 'Form 13 Resale or Exempt Sale Certificate', url: 'https://revenue.nebraska.gov/businesses/sales-and-use-tax-forms', committed: [cf('NE-form-13.pdf', 'Form 13', 'Form 13 Resale or Exempt Sale Certificate')] },
  NV: { form: 'Resale certificate', url: 'https://tax.nv.gov/Forms/General_Purpose_Forms/' },
  NJ: { form: 'ST-3 Resale Certificate', url: 'https://www.nj.gov/treasury/taxation/pdf/other_forms/sales/st3.pdf', committed: [cf('NJ-st-3.pdf', 'ST-3', 'ST-3 Resale Certificate')] },
  NM: { form: 'Nontaxable Transaction Certificate (NTTC)', url: 'https://www.tax.newmexico.gov/businesses/non-taxable-transaction-certificates-nttc/' },
  NY: { form: 'ST-120 Resale Certificate; ST-121 Exempt Use', url: 'https://www.tax.ny.gov/forms/sales_tax_exemption_forms.htm', committed: [cf('NY-st-120.pdf', 'ST-120', 'ST-120 Resale Certificate'), cf('NY-st-120-1.pdf', 'ST-120.1', 'ST-120.1 Contractor Exempt Purchase Certificate'), cf('NY-st-121.pdf', 'ST-121', 'ST-121 Exempt Use Certificate')] },
  NC: { form: 'E-595E Streamlined Certificate of Exemption', url: 'https://www.ncdor.gov/documents/form-e-595e-streamlined-sales-and-use-tax-certificate-exemption' },
  ND: { form: 'Streamlined Certificate of Exemption', url: 'https://www.tax.nd.gov/business/sales-and-use-tax/exemptions' },
  OH: { form: 'STEC B Blanket Exemption Certificate', url: 'https://tax.ohio.gov/business/ohio-business-taxes/sales-and-use/exemption-certificate-forms', committed: [cf('OH-stec-b.pdf', 'STEC B', 'STEC B Blanket Exemption Certificate (2025)'), cf('OH-stec-b-prior.pdf', 'STEC B', 'STEC B Blanket Exemption Certificate (prior revision)')] },
  OK: { form: 'Streamlined Certificate of Exemption', url: 'https://oklahoma.gov/tax/businesses/sales-use-tax.html' },
  PA: { form: 'REV-1220 Pennsylvania Exemption Certificate', url: 'https://www.pa.gov/agencies/revenue/forms-and-publications/forms-for-businesses/sales-use-and-hotel-occupancy-tax-forms.html', committed: [cf('PA-rev-1220.pdf', 'REV-1220', 'REV-1220 Pennsylvania Exemption Certificate')] },
  RI: { form: 'Resale and exemption certificates', url: 'https://tax.ri.gov/forms/business-tax-forms/sales-excise-tax-forms', committed: [cf('RI-resale-certificate.pdf', 'RI Resale', 'Sales and Use Tax Resale Certificate')] },
  SC: { form: 'ST-8 Exemption Certificate; ST-8A Resale', url: 'https://dor.sc.gov/forms-site/Pages/default.aspx', committed: [cf('SC-st-8.pdf', 'ST-8', 'ST-8 Exemption Certificate (single sale)'), cf('SC-st-8a.pdf', 'ST-8A', 'ST-8A Resale Certificate')] },
  SD: { form: 'Streamlined Certificate of Exemption', url: 'https://dor.sd.gov/businesses/taxes/sales-use-tax/' },
  TN: { form: 'Streamlined Certificate of Exemption', url: 'https://www.tn.gov/revenue/taxes/sales-and-use-tax/forms.html', committed: [cf('TN-rv-f1301301.pdf', 'RV-F1301301', 'RV-F1301301 Government Certificate of Exemption')] },
  TX: { form: '01-339 Sales and Use Tax Resale / Exemption Certification', url: 'https://comptroller.texas.gov/forms/01-339.pdf', committed: [cf('TX-01-339.pdf', '01-339', '01-339 Sales and Use Tax Resale Certificate')] },
  UT: { form: 'TC-721 Exemption Certificate', url: 'https://tax.utah.gov/forms/current/tc-721.pdf', committed: [cf('UT-tc-721.pdf', 'TC-721', 'TC-721 Exemption Certificate'), cf('UT-tc-721g.pdf', 'TC-721G', 'TC-721G Exemption Certificate for Governments and Schools')] },
  VT: { form: 'S-3 Resale and Exempt Organization Certificate', url: 'https://tax.vermont.gov/business/sales-and-use-tax/exemption-certificates' },
  VA: { form: 'ST-10 Resale; ST-11 Manufacturing; ST-12 Government', url: 'https://www.tax.virginia.gov/sales-and-use-tax-exemptions' },
  WA: { form: "Buyer's Retail Sales Tax Exemption Certificate", url: 'https://dor.wa.gov/taxes-rates/retail-sales-tax/exemptions' },
  WV: { form: 'Streamlined Certificate of Exemption (F0003)', url: 'https://tax.wv.gov/Business/SalesAndUseTax/Pages/SalesAndUseTax.aspx' },
  WI: { form: 'S-211 Sales and Use Tax Exemption Certificate', url: 'https://www.revenue.wi.gov/Pages/Form/sales-home.aspx' },
  WY: { form: 'Streamlined Certificate of Exemption', url: 'https://revenue.wyo.gov/divisions/excise-tax/forms-and-publications' },
};

/**
 * The Streamlined Sales Tax member states by code, the 23 full members and the one associate, Tennessee (T862;
 * the product owner, 2026-10-02: *"pa is not on the sst why is that form showed"*). Source:
 * https://www.streamlinedsalestax.org/about-us/state-info, the Governing Board's state list, read 2026-10-02 from
 * the builder's own reference because the build machine could not reach the site; a change in membership is one
 * edit here.
 */
export const STREAMLINED_MEMBER_STATES: readonly string[] = [
  'AR', 'GA', 'IN', 'IA', 'KS', 'KY', 'MI', 'MN', 'NE', 'NV', 'NJ', 'NC', 'ND', 'OH', 'OK', 'RI', 'SD', 'TN', 'UT', 'VT', 'WA', 'WV', 'WI', 'WY',
];

/** Whether a state is a Streamlined member, full or associate, so the Streamlined certificate is offered on its page. */
export function isStreamlinedMember(stateCode: string): boolean {
  return STREAMLINED_MEMBER_STATES.includes(stateCode.toUpperCase());
}

/** The multistate form the 24 Streamlined member states accept, offered on a member state's page beside its own. */
export const STREAMLINED_EXEMPTION_FORM: StateExemptionForm = { form: 'Streamlined Sales Tax Certificate of Exemption', url: 'https://www.streamlinedsalestax.org/certificate-of-exemption', committed: [cf('SST-certificate-of-exemption.pdf', 'SST', 'Streamlined Sales Tax Certificate of Exemption')] };

/** The Multistate Tax Commission's resale certificate, which the states it lists accept for resale (decision 0676). */
export const MTC_RESALE_FORM: StateExemptionForm = { form: 'Uniform Sales & Use Tax Resale Certificate — Multijurisdiction', url: 'https://www.mtc.gov/', committed: [cf('MTC-uniform-resale-certificate.pdf', 'MTC', 'Uniform Sales & Use Tax Resale Certificate — Multijurisdiction')] };

/** The two multistate forms. The library and a certificate's form types carry both for every state; a state's page offers the Streamlined one to a member alone. */
export const MULTISTATE_EXEMPTION_FORMS: readonly StateExemptionForm[] = [STREAMLINED_EXEMPTION_FORM, MTC_RESALE_FORM];

/**
 * Whether a state's page offers a multistate form (T862): the Streamlined certificate on a member state's page only,
 * the Multistate Tax Commission's on every page. The certificate reading and the forms library are not narrowed, so
 * a customer can still hand us a Streamlined certificate in any state.
 */
export function offersMultistateForm(stateCode: string, file: string): boolean {
  const streamlined = (STREAMLINED_EXEMPTION_FORM.committed ?? []).some((form) => form.file === file);
  return !streamlined || isStreamlinedMember(stateCode);
}

/** The group a multistate form stands under in the forms library, where a state form stands under its two-letter code. */
export const MULTISTATE_GROUP = 'Multistate';

/** Every committed form with the group it stands under: each state's own in code order, then the two multistate forms. */
export function committedExemptionForms(): readonly (CommittedExemptionForm & { group: string })[] {
  const states = Object.keys(STATE_EXEMPTION_FORMS).sort().flatMap((code) => (STATE_EXEMPTION_FORMS[code]!.committed ?? []).map((form) => ({ ...form, group: code })));
  return [...states, ...MULTISTATE_EXEMPTION_FORMS.flatMap((row) => (row.committed ?? []).map((form) => ({ ...form, group: MULTISTATE_GROUP })))];
}

/**
 * The form types a certificate for a state may carry (decision 0676): the
 * state's own committed forms, then the two multistate forms, one row per code
 * so two revisions of one form offer it once.
 */
export function exemptionFormTypesFor(stateCode: string): readonly { code: string; title: string }[] {
  const own = stateExemptionForm(stateCode)?.committed ?? [];
  const multi = MULTISTATE_EXEMPTION_FORMS.flatMap((row) => row.committed ?? []);
  const seen = new Set<string>();
  return [...own, ...multi].filter((form) => (seen.has(form.code) ? false : (seen.add(form.code), true))).map((form) => ({ code: form.code, title: form.title }));
}

const codeKey = (code: string) => code.toUpperCase().replace(/[\s-]/g, '');

/** A read or typed form code as the forms table writes it, or null where no committed form carries it (decision 0676). */
export function exemptionFormType(code: string | null | undefined): string | null {
  if (!code || !code.trim()) return null;
  const key = codeKey(code);
  return committedExemptionForms().find((form) => codeKey(form.code) === key)?.code ?? null;
}

/** The state's own form, or null where the state levies no general sales tax. */
export function stateExemptionForm(stateCode: string): StateExemptionForm | null {
  return STATE_EXEMPTION_FORMS[stateCode.toUpperCase()] ?? null;
}

/**
 * 🔒 The forms the business hides from the forms library and the certificate
 * modal's links, as the committed file names (decision 0677). It ships empty.
 */
export const HIDDEN_TAX_FORMS_KEY = 'tax.hiddenForms';

/** The hidden list a stored value holds, keeping only names the table carries; anything else reads as none hidden. */
export function hiddenTaxForms(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  const known = new Set(committedExemptionForms().map((form) => form.file));
  return [...new Set(value.filter((name): name is string => typeof name === 'string' && known.has(name)))];
}

/** Why a value cannot be saved as the hidden list, or null: a list of file names the forms table names (decision 0677). */
export function hiddenTaxFormsProblem(value: unknown): string | null {
  if (value === null) return null;
  if (!Array.isArray(value) || value.some((name) => typeof name !== 'string')) return 'the hidden forms are a list of the forms’ file names';
  const known = new Set(committedExemptionForms().map((form) => form.file));
  const unknown = value.filter((name) => !known.has(name as string));
  return unknown.length > 0 ? `${unknown.join(', ')} is not a form in the forms library` : null;
}

/** One form as the library lists it: the table's own facts and whether it is hidden. */
export interface TaxFormRow extends CommittedExemptionForm {
  hidden: boolean;
}

/** The library's answer: one group per state holding committed forms, in code order, then Multistate. */
export interface TaxFormsAnswer {
  groups: readonly { group: string; name: string; forms: readonly TaxFormRow[] }[];
}

/** The library's groups from the table and the hidden list; a state's name is its full name, Multistate its own. */
export function taxFormGroups(hidden: readonly string[]): TaxFormsAnswer['groups'] {
  const out: { group: string; name: string; forms: TaxFormRow[] }[] = [];
  for (const form of committedExemptionForms()) {
    let group = out.find((row) => row.group === form.group);
    if (!group) {
      group = { group: form.group, name: form.group === MULTISTATE_GROUP ? MULTISTATE_GROUP : (US_STATE_NAMES[form.group] ?? form.group), forms: [] };
      out.push(group);
    }
    group.forms.push({ file: form.file, code: form.code, title: form.title, hidden: hidden.includes(form.file) });
  }
  return out;
}
